import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import WhatsAppLogo from "@/components/site/WhatsAppLogo";
import Logo from "@/components/site/Logo";
import { useI18n } from "@/i18n/I18nProvider";
import { NAV } from "@/constants/testIds";

const WA = "https://wa.me/919731852956";
const TEL = "tel:+919731852956";

export default function Navbar() {
  const { t, lang, setLang, languages } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);
const links = [
  { to: "/", label: t.nav.home },
  { to: "/products", label: t.nav.products },
  { to: "/gallery", label: t.nav.gallery },
  { to: "/about", label: t.nav.about },
  { to: "/notice", label: t.nav.notice },
  { to: "/contact", label: t.nav.contact },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms-conditions", label: "Terms & Conditions" },
];
  

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-[0_2px_10px_-4px_rgba(15,23,42,0.08)]"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-5 md:px-10 h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <Logo size={44} />
          <div className="flex flex-col leading-tight">
            <span className="font-display font-semibold text-slate-900 text-[15px] md:text-base">Ramdev Steel</span>
            <span className="hidden md:block text-[10px] tracking-[0.2em] uppercase text-slate-500 font-grotesk">Furniture & Fabrication</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-[12.5px] font-grotesk font-medium uppercase tracking-[0.16em] transition-colors ${
                  isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-3 right-3 -bottom-0.5 h-[2px] bg-[var(--steel)] rounded-full"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-0.5 border border-slate-200 rounded-full p-1 bg-white" data-testid={NAV.lang}>
            {languages.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-3 py-1 text-[10.5px] font-grotesk font-semibold tracking-widest rounded-full transition-colors ${
                  lang === l.code ? "bg-[var(--steel)] text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {l.short}
              </button>
            ))}
          </div>
          <a
            href={WA}
            target="_blank"
            rel="noreferrer"
            data-testid={NAV.cta}
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#1EBE57] text-white text-[12px] font-grotesk font-semibold tracking-[0.14em] uppercase rounded-full transition-colors shadow-sm"
          >
            <WhatsAppLogo size={16} /> WhatsApp
          </a>
          <button
            data-testid={NAV.toggle}
            aria-label="menu"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden w-10 h-10 grid place-items-center border border-slate-200 rounded-full text-slate-900 bg-white"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-slate-200"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.to.replace("/", "") || "home"}`}
                  className="py-3 border-b border-slate-100 font-display text-2xl tracking-tight text-slate-900"
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-3 pt-4">
                <a href={WA} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white text-xs font-grotesk uppercase tracking-widest rounded-full font-semibold">
                  <WhatsAppLogo size={16} /> WhatsApp
                </a>
                <a href={TEL} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 border border-slate-200 text-slate-900 text-xs font-grotesk uppercase tracking-widest rounded-full font-semibold">
                  <Phone size={14} /> Call
                </a>
              </div>
              <div className="flex items-center gap-1 pt-4">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`px-3 py-1.5 text-[11px] font-grotesk font-semibold tracking-widest rounded-full ${
                      lang === l.code ? "bg-[var(--steel)] text-white" : "text-slate-500 border border-slate-200"
                    }`}
                  >
                    {l.short}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
